import type { EasemobChat } from 'easemob-websdk';
import { useTranslation } from 'next-i18next';
import { useState, useRef, useCallback, useMemo } from 'react';

import { postGetRevertConversation, postRestartConversation } from '../api';
import { IMessage, MessageType, RoleType, AudioStatus, AttachmentImage } from '../types';
import { CustomAudioPayload, message } from '../ui';
import {
  useIM,
  EASEMOB_OTHER_USER_LOGGED_CODE,
  CustomError,
  EASEMOB_NOT_LOGIN_CODE,
} from './useIM';

const generateLocalMid = () => `${Date.now()}_${Math.random()}`;

interface IProps {
  app: 'xingye' | 'talkie';
  fromWorkspace?: boolean;
  onConnectedCb?: () => void;
  onSendMessageSuccessCb?: (mid: string, mType: string) => void;
  onResetChatCb?: (lastMid: string) => void;
}

export const useChat = ({
  app,
  fromWorkspace,
  onConnectedCb,
  onSendMessageSuccessCb,
  onResetChatCb,
}: IProps) => {
  const { t } = useTranslation();
  const [messageList, setMessageList] = useState<IMessage[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingCnt, setLoadingCnt] = useState<number>(0);

  const imInitedRef = useRef<boolean>(false);
  const customAudioRef = useRef<any>(null);
  const sendboxRef = useRef<any>(null);
  const messageContainerRef = useRef<any>(null);

  const npcIdRef = useRef<number>();

  const scrollTimer = useRef<any>();

  const { initIM, initIMEventHandler, setNpcImAccount, sendMessage, npcAgentType } = useIM({
    app,
    fromWorkspace,
  });

  const scrollToLastMessage = useCallback(() => {
    if (messageContainerRef && messageContainerRef.current) {
      scrollTimer.current && clearTimeout(scrollTimer.current);
      scrollTimer.current = setTimeout(() => {
        messageContainerRef.current.scrollTo({
          top: messageContainerRef.current.scrollHeight,
          left: 0,
          behavior: 'smooth',
        });
      }, 200);
    }
  }, []);

  const setNpcId = (npcId: number) => {
    npcIdRef.current = npcId;
  };

  const setFirstPrologue = (prologue: string, shouldInsert: boolean = true) => {
    const prologueData = {
      npc_id: npcIdRef.current,
      txt: prologue,
      mid: generateLocalMid(),
      type: MessageType.Text,
      is_open: true,
      wav_status: AudioStatus.Ready,
      role_type: RoleType.AI,
    };

    if (messageList && messageList.length && messageList[0].is_open) {
      setMessageList(
        messageList.map((msg: IMessage, index: number) => {
          if (index === 0) {
            return prologueData;
          }
          return msg;
        })
      );
    } else if (shouldInsert) {
      setMessageList([prologueData, ...(messageList || [])]);
    }
  };

  const onResetChat = useCallback(async () => {
    if (!npcIdRef.current) return;

    try {
      await postRestartConversation({
        npc_id: npcIdRef.current as number,
        from_workspace: fromWorkspace,
      });

      if (!messageList.length) {
        return;
      }

      const _messageList = messageList
        ?.filter((item: IMessage) => item.type !== MessageType.Reset)
        .map((item: IMessage) => {
          return {
            ...item,
            discarded: true,
          };
        });

      onResetChatCb?.(messageList?.[messageList.length - 1]?.mid);

      setMessageList([
        ...(_messageList || []),
        {
          txt: t('edit_chat_preview_reset_tip'),
          type: MessageType.Reset,
          mid: generateLocalMid(),
        } as any,
      ]);

      scrollToLastMessage();
    } catch (err) {
      console.error('onReset', err);
      message.error((err as any)?.message);
    }
  }, [messageList]);

  const onSendMessage = (val: string, attachment?: AttachmentImage | null) => {
    const messageParams = {
      txt: val,
      type: attachment && attachment.url ? MessageType.Attachment : MessageType.Text,
      role_type: RoleType.User,
      mid: generateLocalMid(),
      attachment,
      npc_id: npcIdRef.current,
    };

    sendMessage(messageParams.txt, messageParams.mid, attachment);

    setMessageList([...(messageList || []), messageParams]);

    sendboxRef.current?.setInputValue('');
    sendboxRef.current?.clearAttachment();

    scrollToLastMessage();
  };

  const onClickPlayAudio = async (payload: CustomAudioPayload) => {
    console.log('onclick audio payload', payload);
    if (payload.status === AudioStatus.Playing) {
      onAudioPause(payload.mid);
      customAudioRef.current.pause();
    } else {
      customAudioRef.current.play(payload);
    }
  };

  const onAudioPlayEnd = async (mid: string) => {
    console.log('[onAudioPlayEnd]', mid);
    setMessageList(
      messageList.map((msg: IMessage) => {
        if (msg.mid === mid) {
          return {
            ...msg,
            wav_status: AudioStatus.Ready,
          };
        }
        return msg;
      })
    );
  };

  const onAudioPause = async (mid: string) => {
    console.log('[onAudioPause]', mid);
    setMessageList(
      messageList.map((msg: IMessage) => {
        if (msg.mid === mid) {
          return {
            ...msg,
            wav_status: AudioStatus.Ready,
          };
        }
        return msg;
      })
    );
  };

  const onAudioPlaying = async (mid: string) => {
    setMessageList(
      messageList.map((msg: IMessage) => {
        if (msg.mid === mid) {
          return {
            ...msg,
            wav_status: AudioStatus.Playing,
          };
        }
        return msg;
      })
    );
  };

  const onAudioWaiting = async (mid: string) => {
    console.log('[onAudioWaiting]', mid);
    setMessageList(
      messageList.map((msg: IMessage) => {
        if (msg.mid === mid) {
          return {
            ...msg,
            wav_status: AudioStatus.Loading,
          };
        }
        return msg;
      })
    );
  };

  const onAudioError = async (mid: string, msg?: string) => {
    console.log('[onAudioError]', mid);
    setMessageList(
      messageList.map((msg: IMessage) => {
        if (msg.mid === mid) {
          return {
            ...msg,
            wav_status: AudioStatus.Reload,
          };
        }
        return msg;
      })
    );
  };

  /** IM 事件handler **/
  const onInitSuccess = () => {
    imInitedRef.current = true;
  };

  const onInitError = (err: EasemobChat.ErrorEvent) => {
    try {
      if (err.type === EASEMOB_OTHER_USER_LOGGED_CODE) {
        // 其他地方登录，持续loading
        setLoading(true);
      }
      // const errorData = JSON.parse(err?.data?.data || '{}');
      // message.error(errorData?.error_description || err.message);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log('on easemob 【init】err', err);
    }
  };

  const onReceiveTextMessage = (message: IMessage) => {
    setMessageList([
      ...(messageList || []),
      {
        ...message,
        npc_id: npcIdRef.current as number,
      },
    ]);

    scrollToLastMessage();

    setLoadingCnt((cnt) => Math.max(cnt - 1, 0));
  };

  const onReceiveVoiceMessage = (message: Partial<IMessage>) => {
    setMessageList(
      messageList?.map((item: IMessage) => {
        if (item.mid === message.mid) {
          return {
            ...item,
            ...message,
          };
        }
        return item;
      }) || []
    );
  };

  const onReceiveAsideMessage = (_message: IMessage) => {
    setMessageList([
      ...(messageList || []),
      {
        ..._message,
        npc_id: npcIdRef.current as number,
      },
    ]);

    scrollToLastMessage();

    setLoadingCnt((cnt) => Math.max(cnt - 1, 0));
  };

  const onSendMessageSuccess = (mid: string, localMid: string) => {
    let msgType = 'text';

    setMessageList(
      messageList.map((m: IMessage) => {
        if (m.mid === localMid) {
          if (m.attachment) {
            if (m.txt?.length) msgType = 'textpic';
            else msgType = 'pic';
          } else msgType = 'text';

          return { ...m, mid, localMid };
        } else {
          return m;
        }
      })
    );

    setLoadingCnt((cnt) => cnt + 1);

    onSendMessageSuccessCb?.(mid, msgType);
  };

  const onSendMessageError = (error: CustomError, mid: string) => {
    // 只要报错都撤回
    setMessageList(messageList?.filter((_msg: IMessage) => _msg?.mid !== mid));

    if (error.type === EASEMOB_NOT_LOGIN_CODE) {
      // message.error('Please wait for a sec');
    } else {
      // message.error(error.message);
    }
  };

  const onSendCmdMessage = () => {};

  const onConnected = () => {
    onConnectedCb?.();
  };

  const hasLoading = useMemo(() => {
    const has = !!(loadingCnt > 0);
    if (has) {
      scrollToLastMessage();
      return true;
    }

    return false;
  }, [loadingCnt]);

  const onModifiedMessage = useCallback((message: IMessage) => {
    setMessageList((messageList) =>
      messageList.map((m: IMessage) => {
        if (m.mid === message.mid) {
          return { ...m, ...message };
        }
        return m;
      })
    );
  }, []);

  /**
   * 回溯消息
   */
  const recallMsg = useCallback(
    async (mid?: string, isText01?: boolean) => {
      try {
        const targetMessageIndex = messageList.findIndex((m) => m.mid === mid);
        const targetMessage = messageList[targetMessageIndex];

        if (!targetMessage) {
          throw new Error('Message not found: ' + mid);
        }

        await postGetRevertConversation({
          im_mid: isText01 ? undefined : targetMessage.mid,
          custom_mid: targetMessage.custom_mid,
          npc_id: npcIdRef.current as number,
        });

        const newMessageList = messageList.slice(
          0,
          targetMessageIndex + (isText01 ? 1 : targetMessage.role_type === RoleType.User ? 0 : 1)
        );
        setMessageList(newMessageList);
        message.success('操作成功');
      } catch (e: any) {
        console.log('recallMsg error', e);
        throw new Error('This message is too old to be recalled');
      }
    },
    [messageList]
  );

  initIMEventHandler({
    onInitSuccess,
    onInitError,
    onReceiveTextMessage,
    onReceiveVoiceMessage,
    onReceiveAsideMessage,
    onSendMessageSuccess,
    onSendMessageError,
    onSendCmdMessage,
    onConnected,
    onModifiedMessage,
  });

  const appendCustomAsideMessage = useCallback(
    (asideMsg: IMessage) => {
      setMessageList((oldList) => [...oldList, asideMsg]);
      scrollToLastMessage();
    },
    [scrollToLastMessage]
  );

  return {
    messageList,
    customAudioRef,
    sendboxRef,
    messageContainerRef,
    loading,
    loadingCnt,
    hasLoading,
    initIM,
    setMessageList,
    setNpcImAccount,
    setNpcId,
    setFirstPrologue,
    onSendMessage,
    onClickPlayAudio,
    onAudioPlayEnd,
    onAudioPause,
    onAudioPlaying,
    onAudioError,
    onAudioWaiting,
    onResetChat,
    appendCustomAsideMessage,
    scrollToLastMessage,
    npcAgentType,
    recallMsg,
  };
};
